import React, { useEffect, useState } from 'react';
import { tradeLicenseApi } from '../../../services/tradeLicenseApi';

const PLACEHOLDERS = [
  '{owner_name}', '{owner_name_hi}', '{father_name}', '{father_name_hi}',
  '{business_name}', '{business_name_hi}', '{business_type}', '{business_type_hi}',
  '{residence}', '{residence_hi}', '{ward_number}', '{holding_number}',
  '{fee}', '{fee_words}', '{fee_words_hi}', '{payment_date}', '{receipt_number}',
  '{financial_year}', '{valid_till}', '{valid_till_long}', '{valid_till_hi}',
  '{municipality}', '{municipality_hi}', '{act_name}', '{act_name_hi}',
];

const STATEMENT_META = [
  { key: 'statement1', label: 'Statement 1 — Legal Powers' },
  { key: 'statement2', label: 'Statement 2 — Licensee & Establishment' },
  { key: 'statement3', label: 'Statement 3 — Fee & Receipt' },
  { key: 'statement4', label: 'Statement 4 — Validity' },
];

const IMAGE_META = [
  { key: 'signature_image', urlKey: 'signature_image_url', label: 'Officer Signature', hint: 'Transparent PNG recommended (approx 170×45px)' },
  { key: 'seal_image', urlKey: 'seal_image_url', label: 'Official Seal / Stamp', hint: 'Round seal image (approx 200×200px)' },
  { key: 'govt_logo', urlKey: 'govt_logo_url', label: 'Government Emblem (e.g. Bihar Sarkar)', hint: 'Shown at bottom-right of the certificate' },
];

const EMPTY_FORM = {
  slogan_hi_line1: '',
  slogan_hi_line2: '',
  slogan_en_line1: '',
  slogan_en_line2: '',
  act_name_en: '',
  act_name_hi: '',
  signatory_title_en: 'Executive Officer',
  signatory_title_hi: 'कार्यपालक पदाधिकारी',
  govt_logo_caption_en: 'Government of Bihar',
  govt_logo_caption_hi: 'बिहार सरकार',
  statement1_en: '', statement1_hi: '',
  statement2_en: '', statement2_hi: '',
  statement3_en: '', statement3_hi: '',
  statement4_en: '', statement4_hi: '',
};

const CertificateSettings = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [existingUrls, setExistingUrls] = useState({});
  const [files, setFiles] = useState({});
  const [previews, setPreviews] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await tradeLicenseApi.getCertificateConfig();
        if (res.data.configured) {
          const next = { ...EMPTY_FORM };
          Object.keys(EMPTY_FORM).forEach((k) => {
            if (res.data[k] != null) next[k] = res.data[k];
          });
          setForm(next);
          setExistingUrls({
            signature_image: res.data.signature_image_url,
            seal_image: res.data.seal_image_url,
            govt_logo: res.data.govt_logo_url,
          });
        }
      } catch (err) {
        console.error('Failed to load certificate config:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFile = (key, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }
    setError('');
    setFiles((prev) => ({ ...prev, [key]: file }));
    setPreviews((prev) => ({ ...prev, [key]: URL.createObjectURL(file) }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v ?? ''));
      Object.entries(files).forEach(([k, f]) => data.append(k, f));
      await tradeLicenseApi.saveCertificateConfig(data);
      setMessage('Certificate configuration saved. Open any certificate to verify.');
      setFiles({});
    } catch (err) {
      const detail = err.response?.data;
      setError(
        typeof detail === 'string'
          ? detail
          : detail?.error || 'Failed to save configuration.'
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">Loading certificate configuration…</div>;

  return (
    <div className="certificate-settings">
      <p className="settings-hint">
        Customise the printed trade license certificate. Every field is optional —
        when a field is left empty the certificate uses its built-in default.
      </p>

      {message && <div className="alert alert-success">{message}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <h3 className="cert-settings-heading">Images</h3>
      <div className="cert-settings-images">
        {IMAGE_META.map(({ key, label, hint }) => (
          <div key={key} className="cert-image-field">
            <label>{label}</label>
            <div className="cert-image-preview">
              {previews[key] || existingUrls[key] ? (
                <img src={previews[key] || existingUrls[key]} alt={label} />
              ) : (
                <span className="cert-image-empty">Not set — default used</span>
              )}
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleFile(key, e.target.files?.[0])}
            />
            <small>{hint}</small>
          </div>
        ))}
      </div>

      <h3 className="cert-settings-heading">Header Slogans (top corners)</h3>
      <p className="settings-hint">
        Shown at the top-left (Hindi) and top-right (English) of the certificate.
        Leave blank to auto-generate from the municipality name (e.g. स्वच्छ नगर / Swachh Nagar).
      </p>
      <div className="cert-settings-grid">
        <div className="form-group">
          <label>Hindi Slogan — Line 1</label>
          <input name="slogan_hi_line1" value={form.slogan_hi_line1} onChange={handleChange} placeholder="स्वच्छ नगर" />
        </div>
        <div className="form-group">
          <label>English Slogan — Line 1</label>
          <input name="slogan_en_line1" value={form.slogan_en_line1} onChange={handleChange} placeholder="Swachh Nagar" />
        </div>
        <div className="form-group">
          <label>Hindi Slogan — Line 2</label>
          <input name="slogan_hi_line2" value={form.slogan_hi_line2} onChange={handleChange} placeholder="सुंदर नगर" />
        </div>
        <div className="form-group">
          <label>English Slogan — Line 2</label>
          <input name="slogan_en_line2" value={form.slogan_en_line2} onChange={handleChange} placeholder="Sundar Nagar" />
        </div>
      </div>

      <h3 className="cert-settings-heading">Governing Act</h3>
      <p className="settings-hint">
        The act under which the license is issued (printed below the certificate title and used
        in the default statements). Leave blank to use &ldquo;Bihar Municipal Act, 2007&rdquo;.
      </p>
      <div className="cert-settings-grid">
        <div className="form-group">
          <label>Act Name (English)</label>
          <input name="act_name_en" value={form.act_name_en} onChange={handleChange} placeholder="e.g. Bihar Municipal Act, 2007" />
        </div>
        <div className="form-group">
          <label>Act Name (Hindi)</label>
          <input name="act_name_hi" value={form.act_name_hi} onChange={handleChange} placeholder="उदा. बिहार नगरपालिका अधिनियम, 2007" />
        </div>
      </div>

      <h3 className="cert-settings-heading">Signatory & Emblem Captions</h3>
      <div className="cert-settings-grid">
        <div className="form-group">
          <label>Signatory Title (English)</label>
          <input name="signatory_title_en" value={form.signatory_title_en} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label>Signatory Title (Hindi)</label>
          <input name="signatory_title_hi" value={form.signatory_title_hi} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label>Emblem Caption (English)</label>
          <input name="govt_logo_caption_en" value={form.govt_logo_caption_en} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label>Emblem Caption (Hindi)</label>
          <input name="govt_logo_caption_hi" value={form.govt_logo_caption_hi} onChange={handleChange} />
        </div>
      </div>

      <h3 className="cert-settings-heading">Certificate Statements</h3>
      <p className="settings-hint">
        Leave blank to use the default auto-generated text. You can use these placeholders —
        they are replaced with live data on each certificate:
      </p>
      <div className="cert-placeholders">
        {PLACEHOLDERS.map((p) => <code key={p}>{p}</code>)}
      </div>

      {STATEMENT_META.map(({ key, label }) => (
        <div key={key} className="cert-statement-block">
          <h4>{label}</h4>
          <div className="form-group">
            <label>Hindi</label>
            <textarea
              name={`${key}_hi`}
              rows={3}
              value={form[`${key}_hi`]}
              onChange={handleChange}
              placeholder="Default Hindi text will be used if left blank"
            />
          </div>
          <div className="form-group">
            <label>English</label>
            <textarea
              name={`${key}_en`}
              rows={3}
              value={form[`${key}_en`]}
              onChange={handleChange}
              placeholder="Default English text will be used if left blank"
            />
          </div>
        </div>
      ))}

      <div className="cert-settings-actions">
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Certificate Configuration'}
        </button>
      </div>
    </div>
  );
};

export default CertificateSettings;
